import { useEffect, useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DownloadIcon from "@mui/icons-material/Download";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import PaymentsIcon from "@mui/icons-material/Payments";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listOrganizations } from "@/api/organizations";
import {
  type TimesheetPeriod,
  type TimesheetStatus,
  exportOrgCsv,
  exportPeriodCsv,
  formatMinutes,
  getPeriodSheet,
  listOrgPeriods,
  markPeriodPaid,
  rejectPeriod,
  reopenPeriod,
  validatePeriod,
} from "@/api/timesheets";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/ToastProvider";
import { usePermission } from "@/auth/usePermission";
import { timesheetStatusColor } from "@/lib/timesheetStatus";

const STATUS_FILTERS: (TimesheetStatus | "ALL")[] = [
  "ALL",
  "SUBMITTED",
  "OPEN",
  "VALIDATED",
  "REJECTED",
  "PAID",
];

/**
 * The manager's queue: who submitted what, what it adds up to, and the
 * validate / send-back / mark-paid decisions that turn a réalisé into a
 * payable fiche.
 */
export function TimesheetsAdminPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const canValidate = usePermission("timesheet:validate");
  const canExport = usePermission("timesheet:export");

  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [orgId, setOrgId] = useState("");
  const [filter, setFilter] = useState<TimesheetStatus | "ALL">("ALL");
  const [sheetId, setSheetId] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<TimesheetPeriod | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [paidTarget, setPaidTarget] = useState<TimesheetPeriod | null>(null);
  const [paidReference, setPaidReference] = useState("");

  const orgs = useQuery({
    queryKey: ["organizations-for-timesheets"],
    queryFn: () => listOrganizations({ limit: 100 }),
  });
  useEffect(() => {
    if (!orgId && orgs.data?.items?.length) setOrgId(orgs.data.items[0]!.id);
  }, [orgs.data, orgId]);

  const periods = useQuery({
    queryKey: ["timesheets", "org", orgId, year, month, filter],
    queryFn: () =>
      listOrgPeriods(orgId, year, month, filter === "ALL" ? undefined : filter),
    enabled: Boolean(orgId),
  });

  const sheet = useQuery({
    queryKey: ["timesheets", "sheet", sheetId],
    queryFn: () => getPeriodSheet(sheetId!),
    enabled: Boolean(sheetId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["timesheets"] });

  const onDecisionError = (e: unknown) => {
    const detail = (e as { response?: { data?: { detail?: unknown } } }).response?.data
      ?.detail;
    if (detail && typeof detail === "object" && "error" in detail) {
      const d = detail as { error: string; count?: number; dates?: string[] };
      if (d.error === "shifts_still_planned") {
        toast(
          t("timesheets.error.shifts_still_planned", {
            count: d.count ?? 0,
            dates: (d.dates ?? []).join(", "),
          }),
          "error",
        );
        return;
      }
      toast(t(`timesheets.error.${d.error}`, { defaultValue: d.error }), "error");
      return;
    }
    toast(
      typeof detail === "string"
        ? t(`timesheets.error.${detail}`, { defaultValue: detail })
        : String(e),
      "error",
    );
  };

  const validateMutation = useMutation({
    mutationFn: (id: string) => validatePeriod(id),
    onSuccess: () => {
      invalidate();
      toast(t("timesheets.validated"), "success");
    },
    onError: onDecisionError,
  });

  const rejectMutation = useMutation({
    mutationFn: () => rejectPeriod(rejectTarget!.id, rejectReason),
    onSuccess: () => {
      invalidate();
      setRejectTarget(null);
      setRejectReason("");
      toast(t("timesheets.rejected"), "info");
    },
    onError: onDecisionError,
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => reopenPeriod(id),
    onSuccess: () => {
      invalidate();
      toast(t("timesheets.reopened"), "info");
    },
    onError: onDecisionError,
  });

  const paidMutation = useMutation({
    mutationFn: () => markPeriodPaid(paidTarget!.id, paidReference || null),
    onSuccess: () => {
      invalidate();
      setPaidTarget(null);
      setPaidReference("");
      toast(t("timesheets.markedPaid"), "success");
    },
    onError: onDecisionError,
  });

  const shiftMonth = (delta: number) => {
    const d = new Date(year, month - 1 + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth() + 1);
  };

  const monthTitle = new Date(year, month - 1, 1).toLocaleString(i18n.language, {
    month: "long",
    year: "numeric",
  });

  return (
    <Box>
      <PageHeader
        title={t("timesheetsAdmin.title")}
        subtitle={t("timesheetsAdmin.subtitle")}
        actions={
          canExport && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              disabled={!orgId}
              onClick={() => exportOrgCsv(orgId, year, month, true)}
            >
              {t("timesheetsAdmin.exportValidated")}
            </Button>
          )
        }
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("planning.organization")}</InputLabel>
          <Select
            label={t("planning.organization")}
            value={orgId}
            onChange={(e) => setOrgId(String(e.target.value))}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <IconButton onClick={() => shiftMonth(-1)} aria-label="previous month">
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ minWidth: 180, textAlign: "center", fontWeight: 600 }}>
            {monthTitle}
          </Typography>
          <IconButton onClick={() => shiftMonth(1)} aria-label="next month">
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>{t("timesheets.col.status")}</InputLabel>
          <Select
            label={t("timesheets.col.status")}
            value={filter}
            onChange={(e) => setFilter(e.target.value as TimesheetStatus | "ALL")}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s} value={s}>
                {s === "ALL" ? t("common.all") : t(`timesheets.status.${s}`)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {periods.isLoading ? (
        <CircularProgress />
      ) : (periods.data ?? []).length === 0 ? (
        <Alert severity="info">{t("timesheetsAdmin.empty")}</Alert>
      ) : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("timesheets.col.practitioner")}</TableCell>
                <TableCell>{t("timesheets.col.status")}</TableCell>
                <TableCell align="right">{t("timesheets.col.shifts")}</TableCell>
                <TableCell align="right">{t("timesheets.col.duration")}</TableCell>
                <TableCell align="right">{t("timesheets.weightedHours")}</TableCell>
                <TableCell align="right">{t("timesheets.stillPlanned")}</TableCell>
                <TableCell>{t("timesheets.col.summary")}</TableCell>
                <TableCell align="right">{t("common.actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(periods.data ?? []).map((p) => {
                const totals = p.totals;
                return (
                  <TableRow key={p.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {p.practitioner_display_name}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={timesheetStatusColor(p.status)}
                        label={t(`timesheets.status.${p.status}`)}
                      />
                    </TableCell>
                    <TableCell align="right">{totals?.realized_shifts ?? 0}</TableCell>
                    <TableCell align="right">
                      {formatMinutes(totals?.total_minutes ?? 0)}
                    </TableCell>
                    <TableCell align="right">
                      {formatMinutes(totals?.weighted_minutes ?? 0)}
                    </TableCell>
                    <TableCell align="right">
                      {totals?.planned_shifts ? (
                        <Chip size="small" color="warning" label={totals.planned_shifts} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 240,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Tooltip title={p.summary ?? ""}>
                        <span>{p.summary ?? "—"}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title={t("timesheetsAdmin.viewSheet")}>
                          <IconButton size="small" onClick={() => setSheetId(p.id)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canExport && (
                          <Tooltip title={t("timesheets.exportCsv")}>
                            <IconButton
                              size="small"
                              onClick={() =>
                                exportPeriodCsv(
                                  p.id,
                                  `releve_${p.practitioner_display_name}_${p.year}-${String(
                                    p.month,
                                  ).padStart(2, "0")}.csv`,
                                )
                              }
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canValidate &&
                          (p.status === "SUBMITTED" || p.status === "OPEN") && (
                            <Tooltip title={t("timesheets.validate")}>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={validateMutation.isPending}
                                onClick={() => validateMutation.mutate(p.id)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        {canValidate && p.status === "SUBMITTED" && (
                          <Tooltip title={t("timesheets.reject")}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRejectTarget(p)}
                            >
                              <UndoIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canValidate && p.status === "VALIDATED" && (
                          <>
                            <Tooltip title={t("timesheets.markPaid")}>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={() => setPaidTarget(p)}
                              >
                                <PaymentsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("timesheets.reopen")}>
                              <IconButton
                                size="small"
                                onClick={() => reopenMutation.mutate(p.id)}
                              >
                                <LockOpenIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ---- Fiche détaillée ---- */}
      <Dialog open={sheetId !== null} onClose={() => setSheetId(null)} fullWidth maxWidth="lg">
        <DialogTitle>
          {sheet.data
            ? t("timesheetsAdmin.sheetTitle", {
                name: sheet.data.period.practitioner_display_name,
                month: `${sheet.data.period.year}-${String(
                  sheet.data.period.month,
                ).padStart(2, "0")}`,
              })
            : t("timesheetsAdmin.viewSheet")}
        </DialogTitle>
        <DialogContent dividers>
          {sheet.isLoading || !sheet.data ? (
            <CircularProgress />
          ) : (
            <Stack spacing={2}>
              {sheet.data.period.summary && (
                <Alert severity="info" icon={false}>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                    {t("timesheets.monthlySummary")}
                  </Typography>
                  {sheet.data.period.summary}
                </Alert>
              )}
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {Object.entries(sheet.data.period.totals?.by_exercise_mode ?? {}).map(
                  ([code, v]) => (
                    <Box key={code}>
                      <Typography variant="caption" color="text.secondary">
                        {code} — {v.label}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {formatMinutes(v.minutes)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {v.shifts} {t("timesheets.shifts")} · {formatMinutes(v.weighted_minutes)}{" "}
                        {t("timesheets.weighted")}
                      </Typography>
                    </Box>
                  ),
                )}
              </Stack>
              <Divider />
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("timesheets.col.date")}</TableCell>
                    <TableCell>{t("timesheets.col.slot")}</TableCell>
                    <TableCell>{t("timesheets.col.mode")}</TableCell>
                    <TableCell>{t("timesheets.col.status")}</TableCell>
                    <TableCell align="right">{t("timesheets.col.duration")}</TableCell>
                    <TableCell align="right">{t("timesheets.weighted")}</TableCell>
                    <TableCell>{t("timesheets.col.mySummary")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sheet.data.lines.map((line) => (
                    <TableRow key={line.assignment_id} hover>
                      <TableCell>{line.date}</TableCell>
                      <TableCell>
                        {line.start_time}–{line.end_time}
                      </TableCell>
                      <TableCell>{line.exercise_mode_code}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(`planning.realized.${line.realized_status}`)}
                        />
                        {line.deviations.length > 0 && (
                          <Typography variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
                            {line.deviations
                              .map((d) => t(`timesheets.deviation.${d}`, { defaultValue: d }))
                              .join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{formatMinutes(line.minutes)}</TableCell>
                      <TableCell align="right">
                        {formatMinutes(line.weighted_minutes)}
                      </TableCell>
                      <TableCell>{line.practitioner_summary ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSheetId(null)}>{t("common.close")}</Button>
          {canValidate &&
            sheet.data &&
            (sheet.data.period.status === "SUBMITTED" ||
              sheet.data.period.status === "OPEN") && (
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  validateMutation.mutate(sheet.data!.period.id);
                  setSheetId(null);
                }}
              >
                {t("timesheets.validate")}
              </Button>
            )}
        </DialogActions>
      </Dialog>

      {/* ---- Renvoi au praticien ---- */}
      <Dialog open={rejectTarget !== null} onClose={() => setRejectTarget(null)} fullWidth>
        <DialogTitle>{t("timesheets.reject")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: 1 }}
            label={t("timesheets.rejectReason")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectReason.trim() || rejectMutation.isPending}
            onClick={() => rejectMutation.mutate()}
          >
            {t("timesheets.reject")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Marquer payé ---- */}
      <Dialog open={paidTarget !== null} onClose={() => setPaidTarget(null)} fullWidth>
        <DialogTitle>{t("timesheets.markPaid")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
            label={t("timesheets.payrollReference")}
            helperText={t("timesheets.payrollReferenceHelp")}
            value={paidReference}
            onChange={(e) => setPaidReference(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaidTarget(null)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            disabled={paidMutation.isPending}
            onClick={() => paidMutation.mutate()}
          >
            {t("timesheets.markPaid")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
